import { createFileRoute, Link } from '@tanstack/react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Skeleton } from '@/components/ui/skeleton';
import { useMassMigrationBatchAuto } from '@/features/mass-migration/hooks/useMassMigrationBatches';
import { InFlightView } from '@/features/mass-migration/reporting/InFlightView';
import { CompletionView } from '@/features/mass-migration/reporting/CompletionView';

export const Route = createFileRoute('/migrations/batches/$batchId')({
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { batchId } = Route.useParams();

  const {
    data: batch,
    isLoading,
    isError,
    error,
    refetch,
  } = useMassMigrationBatchAuto(batchId);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container max-w-5xl py-8 space-y-6">
          <div className="flex items-center gap-3">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-24 w-full rounded-lg" />
          <div className="grid grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-md" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-md" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !batch) {
    return (
      <AppLayout>
        <div className="container max-w-5xl py-8">
          <ErrorMessage
            title="Failed to Load Batch"
            error={error ?? 'Batch not found'}
            onRetry={() => void refetch()}
          />
        </div>
      </AppLayout>
    );
  }

  function renderContent() {
    if (!batch) return null;

    if (batch.status === 'InProgress') {
      return <InFlightView batch={batch} />;
    }

    if (
      batch.status === 'Succeeded' ||
      batch.status === 'Failed' ||
      batch.status === 'Partial' ||
      batch.status === 'Cancelled'
    ) {
      return <CompletionView batch={batch} />;
    }

    if (batch.status === 'Pending') {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center space-y-2">
            <LoadingSpinner size="md" label="Batch is queued..." />
            <p className="text-sm text-muted-foreground">
              This batch is queued and will start shortly.
            </p>
          </div>
        </div>
      );
    }

    return (
      <p className="text-sm text-muted-foreground">
        Unknown batch status: {batch.status}
      </p>
    );
  }

  return (
    <AppLayout>
      <div className="container max-w-5xl py-8 space-y-6">
        {/* Back link */}
        <Link
          to="/migrations"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to Migrations
        </Link>

        {renderContent()}
      </div>
    </AppLayout>
  );
}
